/**
 * AI Level Badge — surfaces current `ftaSettings.aiLevel` in the Project Context Bar.
 * Reactive to localStorage changes (same Settings source of truth).
 *
 * See: .kiro/specs/phase1-tier1-ux-fixes/requirements.md Requirement 3
 *
 * @module components/AILevelBadge
 */
import { useEffect, useState } from 'react';
import { readFTASettings, type AILevel } from '../pages/ftaSettings';

interface BadgeConfig {
  icon: string;
  short: string;
  full: string;
  title: string;
}

const BADGE_CONFIG: Record<AILevel, BadgeConfig> = {
  manual: {
    icon: '✋',
    short: 'Manual',
    full: 'Manual',
    title: 'Manual mode — pure manual authoring. AI features hidden.',
  },
  assisted: {
    icon: '🔍',
    short: 'Assisted',
    full: 'Assisted',
    title: 'Assisted mode — manual + engineering checks (SPF, confidence, circular). No AI generation.',
  },
  augmented: {
    icon: '🤖',
    short: 'AI+',
    full: 'AI-Augmented',
    title: 'AI-Augmented — AI generates drafts, engineer reviews and decides.',
  },
  'full-pipeline': {
    icon: '🚀',
    short: 'Pipeline',
    full: 'Full Pipeline',
    title: 'Full Pipeline — DSR generation, typed repair actions, rule engine veto, pre-reasoning.',
  },
};

export function AILevelBadge() {
  const [level, setLevel] = useState<AILevel>(() => readFTASettings().aiLevel);

  useEffect(() => {
    const refresh = () => setLevel(readFTASettings().aiLevel);

    // Cross-tab sync via storage event
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'sn-settings' || e.key === null) refresh();
    };
    window.addEventListener('storage', onStorage);

    // Same-tab sync via custom event (dispatched by Settings page after save)
    window.addEventListener('sn:settings-change', refresh);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('sn:settings-change', refresh);
    };
  }, []);

  const cfg = BADGE_CONFIG[level];

  return (
    <a
      href="/settings"
      title={cfg.title}
      aria-label={`AI Level: ${cfg.full}. Click to change.`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]
                 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300
                 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer
                 transition-colors no-underline"
    >
      <span aria-hidden="true">{cfg.icon}</span>
      <span className="font-medium">{cfg.short}</span>
    </a>
  );
}
